import React from "react";
import produce from "immer";
import { Box, Typography, TextField, Button, Autocomplete } from "@mui/material";

import { getHmisFormData } from "../../../../actions/hmis";
import { postGenerateTestReport } from "../../../../api/hmis";
import { commonErrorMessage } from "../../../../helpers/messages";
import { resourceCentreActions as actions } from "../../../../actions";
import * as NotificationActions from "../../../../actions/notification";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import DebouncedTextField from "../../../../components/DebouncedTextField";
import useCurrentResourceCentre from "../../../../hooks/useCurrentResourceCentre";
import { HmisFormData } from "../../../../interfaces/HmisInterface";

// eslint-disable-next-line import/prefer-default-export
export function HmisSettings(): React.ReactNode {
  const dispatch = useAppDispatch();
  const resourceCentre = useCurrentResourceCentre();
  const formData = useAppSelector((state) => state.hmis?.formData || []);

  React.useEffect(() => {
    dispatch(getHmisFormData());
  }, [dispatch]);

  const formDataDetails = React.useMemo(
    () =>
      (resourceCentre.settings?.hmisSettings?.hmisFormDataIds || [])
        .map((id) => formData.find((item: HmisFormData) => item.id === id))
        .filter(Boolean),
    [resourceCentre.settings?.hmisSettings?.hmisFormDataIds, formData]
  );

  const generateTestReport = async () => {
    try {
      await postGenerateTestReport(resourceCentre.id);
      dispatch(
        NotificationActions.notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "success",
          message: "Report Generated Successfully",
          autoTimeout: true
        })
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      dispatch(
        NotificationActions.notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: error?.data?.message || commonErrorMessage,
          autoTimeout: true
        })
      );
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography sx={{ width: "150px", flexShrink: 0 }}>Username</Typography>
        <DebouncedTextField
          variant="outlined"
          placeholder="Enter username"
          slotProps={{
            inputLabel: { shrink: true }
          }}
          fullWidth
          value={resourceCentre.settings.hmisSettings.username || ""}
          debounceAt={1000}
          onChange={(e) => {
            const newRc = produce(resourceCentre, (draft) => {
              draft.settings.hmisSettings.username = e.target.value;
            });
            dispatch(actions.putResourceCentre(newRc));
          }}
        />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography sx={{ width: "150px", flexShrink: 0 }}>Password</Typography>
        <DebouncedTextField
          type="password"
          variant="outlined"
          placeholder="Enter password"
          slotProps={{
            inputLabel: { shrink: true }
          }}
          fullWidth
          value={resourceCentre.settings.hmisSettings.password || ""}
          debounceAt={1000}
          onChange={(e) => {
            const newRc = produce(resourceCentre, (draft) => {
              draft.settings.hmisSettings.password = e.target.value;
            });
            dispatch(actions.putResourceCentre(newRc));
          }}
        />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography sx={{ width: "150px", flexShrink: 0 }}>OrgUnit</Typography>
        <DebouncedTextField
          variant="outlined"
          placeholder="Enter org unit"
          slotProps={{
            inputLabel: { shrink: true }
          }}
          fullWidth
          value={resourceCentre.settings.hmisSettings.orgUnit || ""}
          debounceAt={1000}
          onChange={(e) => {
            const newRc = produce(resourceCentre, (draft) => {
              draft.settings.hmisSettings.orgUnit = e.target.value;
            });
            dispatch(actions.putResourceCentre(newRc));
          }}
        />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography sx={{ width: "150px", flexShrink: 0, pt: 1 }}>Hmis Elements</Typography>
        <Autocomplete
          multiple
          fullWidth
          options={formData}
          getOptionLabel={(option) => option?.categoryName || ""}
          filterSelectedOptions
          value={formDataDetails}
          renderInput={(params) => (
            <TextField
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...params}
              variant="outlined"
              label="Form Data"
              placeholder="Type form data"
              slotProps={{
                inputLabel: { shrink: true }
              }}
            />
          )}
          size="small"
          onChange={(_, e) => {
            const dataIds = e.map((el) => el?.id);
            const newRc = produce(resourceCentre, (draft) => {
              draft.settings.hmisSettings.hmisFormDataIds = dataIds;
            });
            dispatch(actions.putResourceCentre(newRc));
          }}
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" color="primary" onClick={generateTestReport}>
          Generate Test Report
        </Button>
      </Box>
    </Box>
  );
}
